import { ISlice, IRange } from './constants'

export const createFragment = <T>(
  start: number,
  length: number,
  initial: Partial<ISlice<T>> = {},
): ISlice<T> => ({
  range: createRange(start, length),
  ...initial,
})

export const createRange = (start: number, length = 0): IRange => {
  if (length < 0) {
    throw new Error('range length can not be less than zero')
  }
  return { start, length, end: start + length }
}

export const shrinkRange = (range: IRange): IRange | null => {
  if (range.length < 2) {
    return null
  }
  return createRange(range.start + 1, range.length - 2)
}

export const isRangesEqual = (a: IRange | null, b: IRange | null): boolean => {
  if (a === null || b === null) {
    return false
  }
  return a.start === b.start && a.length === b.length
}

export const isRangeAIncludesB = (a: IRange | null, b: IRange | null): boolean => {
  if (a === null || b === null) {
    return false
  }
  return a.start <= b.start && b.end <= a.end
}

export const isRangesIntersected = (a: IRange | null, b: IRange | null): boolean => {
  if (a === null || b === null || a.start >= b.end || a.end <= b.start) {
    return false
  }
  return true
}

export const getRangesIntersection = (a: IRange, b: IRange): IRange | undefined => {
  if (isRangeAIncludesB(a, b)) {
    return b
  }
  if (isRangeAIncludesB(b, a)) {
    return a
  }
  if (b.end > a.start && b.start < a.start) {
    return createRange(a.start, b.end - a.start)
  }
  if (b.start < a.end && b.end > a.end) {
    return createRange(b.start, a.end - b.start)
  }
  return
}

export const concatRanges = (a: IRange, b: IRange): IRange | undefined => {
  if (a.start > b.end || b.start > a.end) {
    return
  }
  const start = Math.min(a.start, b.start)
  const end = Math.max(a.end, b.end)
  return createRange(start, end - start)
}

export const subtractRanges = (a: IRange, b: IRange): [IRange, IRange?] => {
  if (a.start < b.start && a.end > b.end) {
    return [createRange(a.start, b.start - a.start), createRange(b.end, a.end - b.end)]
  }
  if (isRangeAIncludesB(b, a)) {
    return [createRange(0)]
  }
  if (a.start < b.start && a.end > b.start) {
    return [createRange(a.start, b.start - a.start)]
  }
  if (a.start < b.end && a.end > b.end) {
    return [createRange(b.end, a.end - b.end)]
  }
  return [a]
}
